const { computePosition, shift, arrow, flip, offset, autoUpdate } = window.FloatingUIDOM;

const detectMob = () => {
    return window.innerWidth <= 750
}

const ARROW_SELECTOR = '.floating-arrow';
const ANNOUNCEMENT_BAR_LINK_CLASS = 'announcement-bar__side_link';

const offsetStrategy = (anchorElement) => {
    if (anchorElement.classList.contains(ANNOUNCEMENT_BAR_LINK_CLASS)) {
        return 6.5;
    }
    return 10;
}

const staticSideStrategy = (anchorElement) => {
    if (anchorElement.classList.contains(ANNOUNCEMENT_BAR_LINK_CLASS)) {
        return '-3px';
    }
    return '-4px';
}

const trackedButtons = new Map();
const trackedTooltips = new Map();
const supportOutsideListeners = new WeakSet();
const TOOLTIP_SELECTORS = [
    '#support-tooltip',
    '#chat-unavailable-tooltip',
    '#account-login-tooltip',
    'cart-drawer',
];

function trackButton(buttonSelector, tooltipSelector, elementName, closeList = [], immediatelyShow = false) {
    if (detectMob()) return null;

    const key = `${buttonSelector}::${tooltipSelector}`;
    const existing = trackedButtons.get(key);
    if (existing) {
        if (elementName && existing.bindModal) existing.bindModal(elementName);
        if (immediatelyShow) existing.show();
        return existing;
    }

    const button = document.querySelector(buttonSelector);
    const tooltip = document.querySelector(tooltipSelector);

    if (!button || !tooltip) {
        console.log(`%c Tried to find element with selector ${buttonSelector} but couldn't find it.`, 'background: #222; color: red');
        console.log('button:', button);
        console.log(`%c Tried to find element with selector ${tooltipSelector} but couldn't find it.`, 'background: #222; color: red');
        console.log('tooltip:', tooltip);
        return null;
    }

    const arrowEl = tooltip.querySelector(ARROW_SELECTOR);
    const offsetAmount = offsetStrategy(button);
    const selectorsToClose = closeList.length ? closeList : TOOLTIP_SELECTORS;
    let updateCleanup = null;
    let autoUpdateActive = false;
    let mouseTracking = false;
    const modalNames = new Set();

    const stopAutoUpdate = () => {
        if (typeof updateCleanup === 'function') updateCleanup();
        updateCleanup = null;
        autoUpdateActive = false;
    };

    const stopMouseTracking = () => {
        if (!mouseTracking) return;
        document.removeEventListener('mousemove', trackMouse);
        mouseTracking = false;
    };

    const hideTooltip = () => {
        tooltip.style.display = 'none';
        tooltip.style.visibility = 'hidden';
        stopMouseTracking();
        stopAutoUpdate();
    };

    const updatePosition = () => {
        const middleware = [
            flip(),
            shift(),
            offset(offsetAmount),
        ];
        if (arrowEl) middleware.push(arrow({ element: arrowEl }));

        computePosition(button, tooltip, {
            placement: 'bottom',
            middleware,
        }).then(({ x, y, placement, middlewareData }) => {
            Object.assign(tooltip.style, {
                left: `${x}px`,
                top: `${y}px`,
            });

            if (!arrowEl) return;
            const { x: arrowX, y: arrowY } = middlewareData.arrow || {};
            const staticSide = {
                top: 'bottom',
                right: 'left',
                bottom: 'top',
                left: 'right',
            }[placement.split('-')[0]];

            Object.assign(arrowEl.style, {
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: staticSideStrategy(button),
            });
        }).catch(() => {
            // A tooltip can be removed during a section/cart rerender.
        });
    };

    const startAutoUpdate = () => {
        if (autoUpdateActive) return;
        autoUpdateActive = true;
        try {
            updateCleanup = autoUpdate(button, tooltip, updatePosition);
        } catch (error) {
            autoUpdateActive = false;
            updateCleanup = null;
        }
    };

    const closeOtherTooltips = () => {
        selectorsToClose.forEach((selector) => {
            if (selector === tooltipSelector) return;
            const otherControllers = trackedTooltips.get(selector);
            if (otherControllers && otherControllers.size) {
                otherControllers.forEach((controller) => controller.hide());
                return;
            }
            const otherTooltip = document.querySelector(selector);
            if (!otherTooltip) return;
            otherTooltip.style.display = 'none';
            otherTooltip.style.visibility = 'hidden';
        });
    };

    const showTooltip = () => {
        if (tooltip.classList.contains('disabled')) {
            hideTooltip();
            return false;
        }
        closeOtherTooltips();
        tooltip.style.display = 'block';
        tooltip.style.visibility = 'visible';
        startAutoUpdate();
        return true;
    };

    const trackMouse = (event) => {
        const buttonRect = button.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        const isWithinExtendedRect = event.clientX > tooltipRect.left &&
            event.clientX < tooltipRect.right &&
            event.clientY > buttonRect.top &&
            event.clientY < tooltipRect.bottom;

        if (!isWithinExtendedRect) hideTooltip();
    };

    const beginMouseTracking = () => {
        if (mouseTracking) return;
        document.addEventListener('mousemove', trackMouse);
        mouseTracking = true;
    };

    const onMouseEnter = () => {
        if (!showTooltip()) return;
        requestAnimationFrame(() => {
            if (tooltip.style.visibility === 'visible') beginMouseTracking();
        });
    };

    button.addEventListener('mouseenter', onMouseEnter);

    // Modal listeners are registered once per tracked button, outside the
    // positioning callback. autoUpdate may invoke updatePosition many times.
    const bindModal = (name) => {
        if (!name || modalNames.has(name)) return;
        modalNames.add(name);
        document.addEventListener(`modal-event-${name}-close`, hideTooltip);
        document.addEventListener(`modal-event-${name}-open`, showTooltip);
    };
    bindModal(elementName);

    const controller = {
        bindModal,
        show: showTooltip,
        hide: hideTooltip,
        destroy: () => {
            hideTooltip();
            stopAutoUpdate();
            button.removeEventListener('mouseenter', onMouseEnter);
            modalNames.forEach((name) => {
                document.removeEventListener(`modal-event-${name}-close`, hideTooltip);
                document.removeEventListener(`modal-event-${name}-open`, showTooltip);
            });
            modalNames.clear();
            trackedButtons.delete(key);
            const controllers = trackedTooltips.get(tooltipSelector);
            if (controllers) {
                controllers.delete(controller);
                if (!controllers.size) trackedTooltips.delete(tooltipSelector);
            }
        },
    };
    trackedButtons.set(key, controller);
    if (!trackedTooltips.has(tooltipSelector)) trackedTooltips.set(tooltipSelector, new Set());
    trackedTooltips.get(tooltipSelector).add(controller);

    if (immediatelyShow) showTooltip();
    return controller;
}


function showDropdownMenu(supportDropdownIcon, supportTooltip) {
    trackButton(supportDropdownIcon, supportTooltip, supportTooltip, [], true);
}

function setFocusToSupportMenu(supportTooltip) {
    const wrap = document.querySelector(supportTooltip)
    if (!wrap) return
    const focusableElementsSelector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    const focusableElements = wrap.querySelectorAll(focusableElementsSelector)
    const visibleFocusableElements = Array.from(focusableElements).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0)
    if (visibleFocusableElements && visibleFocusableElements.length > 0) {
        visibleFocusableElements[0].focus()
    }
}

function listerToSupportOut(supportDropdownIcon) {
    const excludedSelector = '[data-header-support-icon], #support-tooltip';
    const supportTooltip = document.querySelector('#support-tooltip');
    if (!supportDropdownIcon || !supportTooltip || supportOutsideListeners.has(supportDropdownIcon)) return;
    supportOutsideListeners.add(supportDropdownIcon);

    supportDropdownIcon.addEventListener('keydown', (event) => {
        if (event.keyCode === 9 || event.code === 'Tab' || event.code === 'Escape') {
            supportTooltip.style.display = 'none';
            supportTooltip.style.visibility = 'hidden';
        }
    });
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!target || typeof target.closest !== 'function' || !target.closest(excludedSelector)) {
            supportTooltip.style.display = 'none';
            supportTooltip.style.visibility = 'hidden';
        }
    });
    supportTooltip.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            supportTooltip.style.display = 'none';
            supportTooltip.style.visibility = 'hidden';
            const dropIcon = document.querySelector('[data-header-support-icon]');
            if (dropIcon) dropIcon.focus();
        }
    });
}

const initializeSupportMenu = () => {
    const supportDropdownIcon = document.querySelector('[data-header-support-icon]');
    if (!supportDropdownIcon) return;
    supportDropdownIcon.addEventListener('mouseover', () => showDropdownMenu('[data-header-support-icon]', '#support-tooltip'));
    supportDropdownIcon.addEventListener('keydown', (event) => {
        if (['Enter'].includes(event.key) || event.code === 'Space') {
            showDropdownMenu('[data-header-support-icon]', '#support-tooltip');
            setFocusToSupportMenu('#support-tooltip');
        }
    });
    listerToSupportOut(supportDropdownIcon);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeSupportMenu, { once: true });
} else {
    initializeSupportMenu();
}

trackButton('[data-header-support-link]', '#support-tooltip');
trackButton('[data-header-support-icon]', '#support-tooltip');
trackButton('.header__chat_live', '#chat-unavailable-tooltip');
trackButton('.header-onscroll__chat_live_link', '#chat-unavailable-tooltip');
trackButton('.header__icons .header__icon', '#account-login-tooltip');

trackButton('#cart-icon-bubble', 'cart-drawer', 'cart-drawer');
